import { useCallback, useMemo } from "react";
import { NetworkStatus, useQuery } from "@apollo/client";
import { GET_ALLOCATIONS_INFINITE_SCROLL } from "graphql/allocation";
import type { AllocationNode } from "utils/allocations/allocationOptions";

const PAGE_SIZE = 100;

type AllocationsPage = {
  allocationsInfiniteScroll: {
    edges: { cursor: string; node: AllocationNode }[];
    pageInfo: { hasNextPage: boolean; endCursor: string | null };
  };
};

/**
 * One allocation type's rows for the admin page, hidden ones included, a page at a time. The page
 * scrolls, so the next page loads from the table's own scroll rather than a box inside it.
 */
export function useAllocationRows({
  allocationType,
  searchTerm,
}: {
  allocationType: { key: string; name: string } | null;
  searchTerm: string;
}) {
  const { data, loading, fetchMore, refetch, networkStatus } =
    useQuery<AllocationsPage>(GET_ALLOCATIONS_INFINITE_SCROLL, {
      variables: {
        first: PAGE_SIZE,
        searchTerm: searchTerm.trim() || undefined,
        allocationType: allocationType
          ? { key: allocationType.key, name: allocationType.name }
          : undefined,
        showAll: true,
      },
      skip: !allocationType,
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
    });

  const connection = data?.allocationsInfiniteScroll;
  const rows = useMemo(
    () => (connection?.edges ?? []).map((edge) => edge.node),
    [connection],
  );
  const fetchingMore = networkStatus === NetworkStatus.fetchMore;

  const loadMore = useCallback(() => {
    if (fetchingMore || !connection?.pageInfo?.hasNextPage) return;
    void fetchMore({
      variables: { after: connection.pageInfo.endCursor },
      updateQuery: (previous, { fetchMoreResult }) => ({
        allocationsInfiniteScroll: {
          ...fetchMoreResult.allocationsInfiniteScroll,
          edges: [
            ...previous.allocationsInfiniteScroll.edges,
            ...fetchMoreResult.allocationsInfiniteScroll.edges,
          ],
        },
      }),
    });
  }, [connection, fetchMore, fetchingMore]);

  return {
    rows,
    loading: loading && !fetchingMore,
    fetchingMore,
    hasNextPage: !!connection?.pageInfo?.hasNextPage,
    loadMore,
    refetch,
  };
}
